import { useEffect, useState } from 'react';

import styles from './CompanyDocuments.module.scss';

import useNavigationStore from '@src/stores/navigationStore';
import BlockTitle from '@src/components/_shared/UI/BlockTitle';
import Container from '@src/components/_shared/Container';
import Subtitle from '@src/components/_shared/UI/Subtitle';
import ValidateButton from '@src/components/_shared/UI/ValidateButton';
import FileUploadInput from '@src/components/_shared/UI/FileUploadInput';
import useRegistrationStore from '@src/stores/registrationStore';
import toast from 'react-hot-toast';
import { MAXSIZEFILE } from '@src/utilities/constants';
import { CompanyDocumentsType, DocumentLabelType } from '@src/utilities/types';
import VirtualAssistant from '@src/assets/VirtualAssistant.png';
import Close from '@src/assets/Close.png';
import Loader from '@src/components/_shared/UI/Loader';
import { useMutation, useQuery } from '@tanstack/react-query';
import { checkIbanStatus, companyFileUpdate, companyFileUpload, getCompanyDocumentsStatus } from '@src/api/Registration';
import { getCompanyDocs } from './companyDocsConfig';
import { handleApiError } from '@src/utilities/functions';

interface CompanyDocumentsStatus {
    type: { name: string; label: string };
    status: string;
    [key: string]: any;
}

export default function CompanyDocuments() {
    const { registration } = useRegistrationStore();
    const { setBlock } = useNavigationStore();
    const [displayModal, setDisplayModal] = useState<boolean>(false);
    const [uploadingFileFrontkey, setUploadingFileFrontkey] = useState<string | null>(null);
    const [lastUpdatedFrontKey, setLastUpdatedFrontKey] = useState<string | null>(null);

    const { data: ibanData, isLoading: ibanLoading } = useQuery({
        queryKey: [`getIbandStatus`],
        queryFn: () => checkIbanStatus({iban: registration.company.ibans[0].iban, companyId: registration.company.company_id}),
    });

    const [docsDataState, setDocsDataState] = useState<CompanyDocumentsType[]>(getCompanyDocs(registration, ibanData));
    const [otherDocs, setOtherDocs] = useState<CompanyDocumentsType[]>([]);

    const { data: companyDocumentsStatus, isSuccess, refetch, isRefetching } = useQuery({
        queryKey: [`getCompanyDocumentsStatus`],
        queryFn: () => getCompanyDocumentsStatus(registration.partner_informations.is_partner ? registration.person_id : undefined),
    });

    const { mutate, isPending } = useMutation({
        mutationFn: (formData: any) => companyFileUpload(formData),
        onSuccess: () => {
            toast.success('Le fichier a été téléchargé avec succès !', { position: 'top-center' });
            setUploadingFileFrontkey(null);
            refetch();
        },
        onError: (error) => {
            handleApiError(error, 'Une erreur est survenue lors du téléchargement du fichier.');
            setUploadingFileFrontkey(null);
            setLastUpdatedFrontKey(null);
        }
    })

    const { mutate: mutateUpdate, isPending: isPendingUpdate } = useMutation({
        mutationFn: ({formData, id}: {formData: any, id: string}) => companyFileUpdate(formData, id),
        onSuccess: () => {
            toast.success('Le fichier a été téléchargé avec succès !', { position: 'top-center' });
            setUploadingFileFrontkey(null);
            refetch();
        },
        onError: (error) => {
            handleApiError(error, 'Une erreur est survenue lors du téléchargement du fichier.');
            setUploadingFileFrontkey(null);
            setLastUpdatedFrontKey(null);
        }
    })

    useEffect(() => {
        if (isSuccess && companyDocumentsStatus.data) {
            const documentsArray = Array.isArray(companyDocumentsStatus.data.documents)
                ? companyDocumentsStatus.data.documents.filter((doc: any) =>
                    // élimine des documents des bénéficiaires effectifs 
                    doc.type.name !== 'address' && doc.type.name !== 'id'
                )
                : [];
            const othersArray = Array.isArray(companyDocumentsStatus.data.others) ?
                companyDocumentsStatus.data.others.map((doc: any) => ({
                    id: doc.id,
                    filename: doc.filename,
                    status: doc.status,
                    size: doc.size ?? null,
                    frontKey: 'extraFile-' + doc.id,
                    type: doc.type.name,
                    type_detail: doc.type_detail.name,
                    title: doc.type_detail.label || "Autre document",
                })) 
                : [];
            setDocsDataState((prevDocs) =>
                prevDocs.map((doc) => {
                    const taxPackageDocs = documentsArray.filter(
                        (doc: CompanyDocumentsStatus) => doc.type_detail.name === 'tax-package'
                    );
                    if (doc.frontKey === 'financialStatements' && taxPackageDocs.length > 0) {
                        const updatedDoc = taxPackageDocs[0];
                        return { 
                            ...doc, 
                            id: updatedDoc.id, 
                            filename: updatedDoc.filename, 
                            size: updatedDoc.size, 
                            status: updatedDoc.status 
                        };
                    }
                    if (doc.frontKey === 'financialStatementsN-1' && taxPackageDocs.length > 1) {
                        const updatedDoc = taxPackageDocs[1];
                        return { 
                            ...doc, 
                            id: updatedDoc.id, 
                            filename: updatedDoc.filename, 
                            size: updatedDoc.size, 
                            status: updatedDoc.status 
                        };
                    }
                    if (doc.type_detail !== 'tax-package') {
                        const updatedDoc = documentsArray.find(
                            (statusDoc: CompanyDocumentsStatus) => statusDoc.type_detail.name === doc.type_detail
                        );
                        return updatedDoc 
                            ? { ...doc, id: updatedDoc.id, filename: updatedDoc.filename, size: updatedDoc.size, status: updatedDoc.status } 
                            : doc;
                    }
                    return doc;
                })
            );
            setOtherDocs(othersArray);
        }
    }, [companyDocumentsStatus]);

    const _handleSelectFile = async (frontKey: string, file: File, type: string, type_detail: string, id?: string) => {
        const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg'];
        if (!allowedTypes.includes(file.type)) {
            return toast.error('Format de fichier non supporté. Veuillez choisir un fichier PDF ou une image (.png, .jpeg).', { position: 'top-center' });
        }
        if (file.size > MAXSIZEFILE) {
            return toast.error('Ce fichier est trop volumineux. La taille maximum est de 10 Mo.', { position: 'top-center'});
        }
        const companyFile = {
            frontKey: frontKey,
            fileName: file.name,
            fileSize: file.size,
            file: file,
            type: type,
            type_detail: type_detail,
            label: file.type === 'application/pdf' ? DocumentLabelType.pdf : DocumentLabelType.image,
        }
        if (frontKey === 'collectiveDecision') {
            toast.error('Attention : Vous devez également joindre les documents d\'identités des associés dans "Autre pièce"', { position: 'top-center' });
        }
        if (companyFile) {
            const formData = new FormData();
            formData.append('file', companyFile.file);
            formData.append('demand_id', registration.demand_id);
            formData.append('type', companyFile.type);
            formData.append('type_detail', companyFile.type_detail);
            if (registration.partner_informations.is_partner) formData.append('is_psi', 'true');
            if (registration.person_id) formData.append('person_id', registration.person_id);
            setUploadingFileFrontkey(frontKey);
            setLastUpdatedFrontKey(frontKey);

            if (id) {
                mutateUpdate({formData, id});
            } else {
                mutate(formData);
            }
        } else {
            toast.error('erreur', { position: 'top-center' });
        }
    };

    const countUploadedFiles = (files: Record<string, any>): number => {
        return Object.keys(files).filter(key => key !== 'others').length;
    };
    
    const _onValidate = () => {
        const uploadedDocsCount = countUploadedFiles(companyDocumentsStatus.data);
        if (uploadedDocsCount < docsDataState.length) {
            return setDisplayModal(true);
        }
        setBlock('Overview');
    };

    if (ibanLoading) {
        return (
            <Container>
                <Loader type="SPINNER" />
            </Container>
        );
    }

    return (
        <Container>
            <BlockTitle title="On y est presque ! Transférez les documents de la société" />
            <Subtitle content="Télécharger les pièces justificatives suivantes" />
            <p className={styles.content}>Fichiers acceptés : .pdf, images (.jpeg, .png)</p>
            {docsDataState.map(({ id, frontKey, filename, size, type, type_detail, title, status }: any) => (
                <FileUploadInput
                    key={frontKey}
                    title={title}
                    documentName={filename}
                    initialFileSize={size ?? 0}
                    isValidated={!!filename}
                    selectFile={(file) => _handleSelectFile(frontKey, file, type, type_detail, id)}
                    isLoading={uploadingFileFrontkey === frontKey || (isRefetching && lastUpdatedFrontKey === frontKey)}
                    accept=".pdf,image/png,image/jpeg"
                    isApproved={status === 'valide'}
                    isCompanyFile
                />
            ))}
            {otherDocs.map(({ id, frontKey, filename, size, type, type_detail, status }: any) => (
                <FileUploadInput
                    key={frontKey}
                    title="Autre pièce"
                    documentName={filename}
                    initialFileSize={size ?? 0}
                    isValidated={!!filename}
                    selectFile={(file) => _handleSelectFile(frontKey, file, type, type_detail, id)}
                    isLoading={uploadingFileFrontkey === frontKey || (isRefetching && lastUpdatedFrontKey === frontKey)}
                    accept=".pdf,image/png,image/jpeg"
                    isApproved={status === 'valide'}
                    isCompanyFile
                />
            ))}
            {/* input d'ajout de pièce supplémentaire (toujours présent) */}
            <FileUploadInput
                key="extraFile"
                title="Autre pièce"
                selectFile={(file) => _handleSelectFile("extraFile", file, "status-acts", "others")}
                isLoading={uploadingFileFrontkey === "extraFile" || (isRefetching && lastUpdatedFrontKey === "extraFile")}
                accept=".pdf,image/png,image/jpeg"
            />
            <ValidateButton title="Continuer" onClick={_onValidate} disabled={!companyDocumentsStatus?.data || !countUploadedFiles(companyDocumentsStatus.data) || isPending || isPendingUpdate} onLoading={isPending || isPendingUpdate} />

            {displayModal ?
                <div className={styles.modalContainer}>
                    <div className={styles.modal}>
                        <div className={styles.close} onClick={() => setDisplayModal(false)}>
                            <img src={Close} alt="Close" width={14} height={14} />
                        </div>
                        <div className={styles.imageContainer}>
                            <img src={VirtualAssistant} alt="Close" width={217} height={183} />
                        </div>
                        <p className={styles.title}>Attention</p>
                        <p className={styles.description}>
                            Pour finaliser votre inscription, un ou plusieurs documents sont encore requis. Vous avez la
                            possibilité de les télécharger dès maintenant pour compléter votre dossier, ou bien de continuer
                            et procéder directement à la signature électronique.
                        </p>
                        <div className={styles.buttons}>
                            <ValidateButton
                                blueGradient
                                isModal
                                title="Revenir et télécharger les documents"
                                onClick={() => setDisplayModal(false)}
                            />
                            <ValidateButton isModal title="Continuer" onClick={() => setBlock('Overview')} />
                        </div>
                    </div>
                </div>
                : null}
        </Container>
    );
}
